'use client';

import { useState, type FC } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { Payment, PaymentStatus } from '@/types/payment';
import { formatCurrency } from '@/shared/lib/utils';
import {
  getPaymentTypeKey,
  getPaymentStatusKey,
  getPaymentProviderKey,
} from '@/shared/lib/payment-formatters';
import { PaymentDetailModal } from './PaymentDetailModal';
import { ArrowLeftRight, ChevronRight, Receipt } from 'lucide-react';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';

/**
 * Status text color (no background) — mirrors Apple's `statusBadge` in
 * `PurchaseHistoryView.swift`. Partial refunds and refunds keep an inline
 * background for emphasis on Apple, but on web we keep the row uniform and
 * use plain colored text for all status values.
 */
function getPaymentStatusTextColor(
  status: PaymentStatus,
  isPartiallyRefunded: boolean
): string {
  if (isPartiallyRefunded) return 'text-warning';
  switch (status) {
    case 'succeeded':
      return 'text-primary';
    case 'failed':
      return 'text-red-600';
    case 'requires_action':
      return 'text-warning';
    case 'pending':
    case 'processing':
      return 'text-amber-500';
    case 'refunded':
    case 'revoked':
      return 'text-red-600';
    case 'canceled':
    case 'expired':
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Icon circle background + foreground — yellow for pending, brand teal for
 * any successful charge (subscription or top-up), red for failed/refunded,
 * gray for canceled/expired, blue for cross-account chain transfers.
 */
function getPaymentIconColor(
  payment: Payment,
  isChainTransfer: boolean
): string {
  if (isChainTransfer) return 'bg-blue-100 text-blue-600';
  const status = payment.attributes.status;
  if (status === 'succeeded') {
    return 'bg-primary/15 text-primary';
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-600';
  }
  if (status === 'canceled' || status === 'expired') {
    return 'bg-muted text-muted-foreground';
  }
  // pending / processing / requires_action (and unknown)
  return 'bg-amber-100 text-amber-500';
}

interface PaymentHistoryItemProps {
  payment: Payment;
}

export const PaymentHistoryItem: FC<PaymentHistoryItemProps> = ({
  payment,
}) => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const t = useTranslations();
  const locale = useLocale();

  // Chain-transfer rows are non-financial audit events surfaced from
  // `subscription_chain_transfers` so the user sees when a chain arrived
  // from / left for another account. They don't have a `/payments/{id}`
  // detail page (links.self is null) so the row is not clickable, no
  // amount/status/chevron is shown, and any totals must skip them.
  const isChainTransfer =
    payment.attributes.type === 'chain_transfer_received' ||
    payment.attributes.type === 'chain_transfer_lost';
  const isFinancial = payment.attributes.is_financial !== false;

  // Plan label: prefer historical snapshot frozen on the payment itself.
  // subscription.plan reflects the CURRENT subscription tier and would
  // mislabel old receipts after an upgrade/downgrade (e.g. show a $35 Light
  // payment as "Pro" once the user upgrades). Fall back to subscription.plan
  // only for legacy rows created before payments.plan_id existed.
  const planName =
    payment.relationships?.plan?.attributes?.name ??
    payment.relationships?.subscription?.relationships?.plan?.attributes?.name;

  // Title: category name ("Subscription", "Token purchase",
  // "Subscription received", "Subscription transferred"). For pass_purchase
  // rows prefer the concrete server-resolved product name ("Pro Pass" /
  // "Tokens Pro Pass"); legacy pass rows without it fall back to the generic
  // localized "Pass purchase" label.
  const paymentTypeKey = getPaymentTypeKey(payment.attributes.type);
  const paymentTypeDisplay =
    payment.attributes.pass_name ?? t(paymentTypeKey);

  // Subtitle detail: plan name for subscriptions; counterparty email for
  // transfers (so the user sees WHICH of their accounts the chain went to /
  // came from — Apple Sign-In Hide-My-Email relays otherwise look identical
  // to "another account"). Falls back to the abstract direction string when
  // the counterparty user has been deleted.
  let subtitleDetail: string | null = null;
  if (isChainTransfer) {
    subtitleDetail =
      payment.attributes.counterparty_email ??
      (payment.attributes.type === 'chain_transfer_lost'
        ? t('paymentHistory.transfer.lost.description')
        : t('paymentHistory.transfer.received.description'));
  } else if (planName) {
    subtitleDetail = `${planName} ${t('paymentDetails.plan')}`;
  }

  // Get translated provider
  const providerKey = getPaymentProviderKey(payment.attributes.provider);
  const providerLabel = t(providerKey);

  // Agent-wallet top-ups are labelled; app-wallet ones are not. An `api`
  // purchase deliberately leaves the visible balance untouched, so without
  // this segment the receipt and the balance contradict each other. Labelling
  // the app wallet too would put a badge on 99% of rows for no information.
  const walletLabel =
    payment.attributes.token_scope === 'api'
      ? t('paymentHistory.wallet.api')
      : null;

  // Format date according to locale - handle invalid dates
  const dateToFormat =
    payment.attributes.paid_at || payment.attributes.created_at;
  const parsedDate = dateToFormat ? new Date(dateToFormat) : null;
  const isValidDate =
    parsedDate &&
    !isNaN(parsedDate.getTime()) &&
    parsedDate.getFullYear() > 1970;

  // Debug logging in development mode
  if (process.env.NODE_ENV === 'development' && !isValidDate) {
    console.warn('Invalid payment date:', {
      paymentId: payment.id,
      paid_at: payment.attributes.paid_at,
      created_at: payment.attributes.created_at,
      parsedDate: parsedDate?.toString(),
    });
  }

  const formattedDate = isValidDate
    ? parsedDate.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  // Icon background + foreground driven by payment status. The icon glyph
  // itself (Receipt vs WalnutIcon) still varies by payment type — see line
  // ~233 below where `isSubscription` selects the glyph.
  const isSubscription =
    payment.attributes.type === 'subscription_initial' ||
    payment.attributes.type === 'subscription_renewal' ||
    payment.attributes.type === 'subscription_upgrade';
  const iconBgClass = getPaymentIconColor(payment, isChainTransfer);

  // Chain-transfer wallet delta (e.g. "+240 tokens", "−240 tokens",
  // "Tokens not moved"). Sign is implied by the row type — no explicit
  // negative sign is composed on the client.
  let tokensMovedDisplay: string | null = null;
  if (isChainTransfer && typeof payment.attributes.tokens_moved === 'number') {
    const count = payment.attributes.tokens_moved;
    if (count <= 0) {
      tokensMovedDisplay = t('paymentHistory.transfer.tokens.skipped');
    } else if (payment.attributes.type === 'chain_transfer_lost') {
      tokensMovedDisplay = t('paymentHistory.transfer.tokens.lost', {
        count,
      });
    } else {
      tokensMovedDisplay = t('paymentHistory.transfer.tokens.received', {
        count,
      });
    }
  }

  // Trailing column (price + status + chevron) is for financial rows only —
  // chain-transfer rows render without a money column to avoid implying the
  // event was a charge.
  const renderTrailing = () => {
    if (!isFinancial) return null;
    const isPartiallyRefunded =
      payment.attributes.is_partially_refunded &&
      payment.attributes.status !== 'canceled';
    const status = payment.attributes.status;
    const statusKey = isPartiallyRefunded
      ? 'paymentHistory.status.partially_refunded'
      : status
        ? getPaymentStatusKey(status)
        : null;
    const statusLabel = statusKey ? t(statusKey) : null;
    const statusTextColor = status
      ? getPaymentStatusTextColor(status, !!isPartiallyRefunded)
      : 'text-muted-foreground';
    return (
      <div className="flex-shrink-0 flex items-center gap-2">
        <div className="text-end">
          <p className="text-list-item-value">
            {payment.attributes.amount !== null &&
            payment.attributes.currency !== null
              ? formatCurrency(
                  payment.attributes.amount / 100,
                  payment.attributes.currency
                )
              : null}
          </p>
          {statusLabel && (
            <p className={`text-list-item-meta mt-0.5 ${statusTextColor}`}>
              {statusLabel}
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground/50 rtl:-scale-x-100" />
      </div>
    );
  };

  const RowContent = (
    <div className="flex items-center gap-3">
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconBgClass}`}
      >
        {isChainTransfer ? (
          <ArrowLeftRight className="w-5 h-5" />
        ) : isSubscription ? (
          <Receipt className="w-5 h-5" />
        ) : (
          <WalnutIcon className="h-5 w-[16px]" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <span className="text-list-item-title">{paymentTypeDisplay}</span>

        <div className="flex gap-1 mt-0.5 text-list-item-meta text-muted-foreground">
          {subtitleDetail && <span>{subtitleDetail}</span>}
          {subtitleDetail && <span>·</span>}
          {walletLabel && <span>{walletLabel}</span>}
          {walletLabel && <span>·</span>}
          <span>{providerLabel}</span>
        </div>

        {tokensMovedDisplay && (
          <span
            className={`text-list-item-meta mt-0.5 block ${
              payment.attributes.type === 'chain_transfer_lost'
                ? 'text-red-600'
                : 'text-primary'
            }`}
          >
            {tokensMovedDisplay}
          </span>
        )}

        <span className="text-list-item-meta text-muted-foreground/60 mt-0.5 block">
          {formattedDate}
        </span>
      </div>

      {renderTrailing()}
    </div>
  );

  if (isChainTransfer) {
    // Non-clickable, no detail modal — there is no /payments/{id} for
    // transfer audit rows.
    return (
      <div className="w-full py-3 border-b border-gray-100 last:border-0 text-start">
        {RowContent}
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsDetailModalOpen(true)}
        className="w-full py-3 border-b border-gray-100 last:border-0 hover:bg-muted/50 transition-colors cursor-pointer text-start"
      >
        {RowContent}
      </button>

      <PaymentDetailModal
        payment={payment}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
      />
    </>
  );
};
